import {getBaseUrl} from "../../utils";

export const PUBLIC_APP_NAME = "On Energy Connect";

/**
 * Design tokens for the public marketing site, per the client's
 * "On Energy Connect — Landing Page Design Specifications" (Oct 2026).
 * Scoped to the public site only — does NOT replace PRIMARY_COLOR/
 * SECONDARY_COLOR in constants/theme.ts, which remain used by the
 * admin/employer dashboards.
 */
export const PUBLIC_COLORS = {
    green: "#1A822A",
    orange: "#FF5500",
    charcoal: "#1E1E1E",
    offWhite: "#F8F9FA",
    white: "#FFFFFF",
};

export const PUBLIC_FONT = {
    heading: "'Poppins', sans-serif",
    body: "'Inter', sans-serif",
};

export const PUBLIC_PRIMARY_BUTTON_STYLE = {
    backgroundColor: PUBLIC_COLORS.green,
    borderColor: PUBLIC_COLORS.green,
    color: "#ffffff",
};

/** Turn stored values such as part_time into Part-time. */
export const prettyEnum = (value?: string) => {
    if (!value) return "";
    const known: Record<string, string> = {
        full_time: "Full-time",
        part_time: "Part-time",
        contract: "Contract",
        freelance: "Freelance",
        internship: "Internship",
        temporary: "Temporary",
    };
    return known[value] ?? value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const PUBLIC_SECONDARY_BUTTON_STYLE = {
    backgroundColor: "transparent",
    borderColor: PUBLIC_COLORS.green,
    borderWidth: "1.5px",
    color: PUBLIC_COLORS.green,
};

export type PublicNavLink = {
    name: string;
    href: string;
    comingSoon?: boolean;
};

export const PUBLIC_NAV_LINKS: PublicNavLink[] = [
    {name: "Home", href: "/"},
    {name: "Jobs & Talent", href: "/jobs"},
    {name: "Energy Marketplace", href: "/marketplace", comingSoon: true},
    {name: "Companies", href: "/companies"},
    {name: "Training", href: "/training"},
    {name: "Download", href: "/#download"},
    {name: "Resources / About", href: "/about"},
];

export type FooterLinkGroup = {
    title: string;
    links: {name: string; href: string}[];
};

export const FOOTER_LINK_GROUPS: FooterLinkGroup[] = [
    {
        title: "Quick Links",
        links: [
            {name: "Home", href: "/"},
            {name: "Jobs & Talent", href: "/jobs"},
            {name: "Energy Marketplace", href: "/marketplace"},
            {name: "Suppliers", href: "/suppliers"},
            {name: "Companies", href: "/companies"},
            {name: "Training", href: "/training"},
        ],
    },
    {
        title: "For Users",
        links: [
            {name: "Create Profile", href: "/auth/signup"},
            {name: "Find Jobs", href: "/jobs"},
            {name: "Find Suppliers", href: "/suppliers"},
            {name: "Help & Support", href: "/about"},
        ],
    },
    {
        title: "For Business",
        links: [
            {name: "List Your Company", href: "/auth/signup"},
            {name: "Become a Supplier", href: "/suppliers"},
            {name: "Advertise With Us", href: "/about"},
        ],
    },
];

export const PUBLIC_SOCIAL_LINKS = [
    {name: "Linkedin", src: "/public/icons/linkedin icon svg.svg", style: {width: "36px", height: "36px"}, href: "https://www.linkedin.com/company/on-energy-groups"},
];

/** Footer "Follow Us" row — icon fonts rather than image assets. */
export const FOOTER_SOCIAL_LINKS = [
    {name: "LinkedIn", icon: "fab fa-linkedin-in", href: "https://www.linkedin.com/company/on-energy-groups"},
    {name: "X", icon: "fab fa-x-twitter", href: "https://x.com/"},
    {name: "Facebook", icon: "fab fa-facebook-f", href: "https://facebook.com/"},
    {name: "Instagram", icon: "fab fa-instagram", href: "https://instagram.com/"},
    {name: "YouTube", icon: "fab fa-youtube", href: "https://youtube.com/"},
];

/** Footer "Contact Us" rows, each with its own icon. */
export const FOOTER_CONTACTS = [
    {icon: "fas fa-phone-alt", value: "+251 11 930 4567", href: "tel:+251119304567"},
    {icon: "fas fa-envelope", value: "info@onenergygroups.com", href: "mailto:info@onenergygroups.com"},
    {icon: "fas fa-map-marker-alt", value: "Addis Ababa, Ethiopia", href: "https://maps.app.goo.gl/gzrdsW2TrPvrJ3m6A"},
];

export const OEC_MAIN_PARTNERS = [
    {
        name: "ADRA Ethiopia",
        website: "https://adra.org/",
        logo: "/public/logos/arda logo.png",
        imgStyle: {height: "8rem", objectFit: "contain" as const}, // Tailwind h-32
    },
    {
        name: "On Energy Consult",
        website: "https://onenergygroups.com/",
        logo: "/public/logos/on energy consult logo.png",
        imgStyle: {height: "9rem", objectFit: "contain" as const}, // Tailwind h-36
    },
];

export const OEC_PARTNERS_LOGO = [
    {
        name: "German Cooperation",
        href: "https://www.giz.de/en",
        src: "/public/logos/german cooperation logo.webp",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "ADRA Ethiopia",
        href: "https://adra.org/",
        src: "/public/logos/arda logo.png",
        style: {height: "4rem", objectFit: "contain" as const}
    },
    {
        name: "On Energy Consult",
        href: "https://onenergygroups.com/",
        src: "/public/logos/on energy consult logo.png",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "World Bank",
        href: "https://www.worldbank.org/",
        src: "/public/logos/world-bank.jpg",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Ethiopian Electric Power",
        href: "https://www.eep.com.et/",
        src: "/public/logos/ethiopian-electric-power.jpg",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Econoler",
        href: "https://www.econoler.com/",
        src: "/public/logos/econoler.png",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Energising Development",
        href: "https://endev.info/",
        src: "/public/logos/energising-development.jpg",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Ethiopian Electric Utility",
        href: "https://www.eeu.gov.et/",
        src: "/public/logos/ethiopian-electric-utility.png",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Sunspot",
        href: "https://onenergygroups.com/",
        src: "/public/logos/sunspot.png",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
    {
        name: "Zoscales Partners",
        href: "https://zoscales.com/",
        src: "/public/logos/zoscales-partners.jpg",
        style: {height: "4.5rem", objectFit: "contain" as const}
    },
];

export const ABOUT_IMPACT_GOALS = [
    "Energy Access",
    "Technical Visibility",
    "Rural Reach"
];


export const CONTACT_DETAILS = [
    {label: "email", value: "info@onenergygroups.com", href: "mailto:info@onenergygroups.com"},
    {
        label: "location",
        value: "Helzer Tower, 4th Floor Bole, Addis Ababa, Ethiopia",
        href: "https://maps.app.goo.gl/gzrdsW2TrPvrJ3m6A"
    },
];

const BASE_URL = getBaseUrl();

export const getAssetUrl = (path?: string) => {
    if (!path) return "/public/company-placeholder.png"; // Fallback image
    return path.startsWith("http") ? path : `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};